import {
  type MoveStopPayload,
  type Move,
  type UpdateMovePayload,
} from '../../../../modules/moves'
import { type Place } from '../../../../common/place'
import {
  type StopFormValues,
  type AddressesFormValues,
  type AddressFormValues,
} from './AddressesForm.type'
import {
  type MoveStop,
  type MoveLocation,
} from '../../../../modules/moves/core/models/subModels/moveLocations.models'

const boolToString = (value?: boolean) => {
  if (value === null) {
    return undefined
  }
  return value ? '1' : '0'
}

const getPlaceFormData = (place: Place, specificAddresses = false) => {
  if ('placeId' in place && specificAddresses) {
    return {
      ...place,
      placeId: undefined,
      placeTextLine1: undefined,
      placeTextLine2: undefined,
    } as unknown as Place
  } else {
    return place
  }
}

type MoveToFormDataOptions = {
  specificAddresses?: boolean
}

const locationToFormData = (location?: MoveLocation | MoveStop, specificAddresses = false) => {
  if (!location) {
    return
  }
  const baseFields = {
    id: location.id,
    address: getPlaceFormData(location.address, specificAddresses),
    nbStairs: location.nbStairs ?? 0,
    elevatorReservationRequired: boolToString(location.elevatorReservationRequired),
  }
  if ('description' in location) {
    return {
      ...baseFields,
      description: location.description,
      stopDurationHours: String(location.stopDurationHours ?? 0),
    }
  }
  return baseFields
}

export const moveToFormData = (
  move: Move,
  {
    specificAddresses = false,
  }: MoveToFormDataOptions = {}): AddressesFormValues => {
  return {
    locations: {
      origin: locationToFormData(move.locations.origin, specificAddresses),
      destination: locationToFormData(move.locations.destination, specificAddresses),
      warehouse: locationToFormData(move.locations.warehouse, specificAddresses),
      stops: (move.locations.stops
        ?.map((stop) => locationToFormData(stop, specificAddresses))
        ?.filter(s => !!s) ?? []) as StopFormValues[],
    },
  }
}

const removeEmptyStrings = <T,>(object: Record<string, any>): T => {
  return Object.fromEntries(Object.entries(object).filter(([key, value]) => {
    return value !== ''
  })) as T
}

const formatLocation = (location?: AddressFormValues | StopFormValues) => {
  if (!location) {
    return
  }
  const baseFields = {
    ...location,
    address: removeEmptyStrings<Place>(location.address),
    elevatorReservationRequired: location.elevatorReservationRequired === undefined ? undefined : location.elevatorReservationRequired === '1',
  }
  if ('description' in location) {
    return {
      ...baseFields,
      description: location.description,
      stopDurationHours: parseFloat(location.stopDurationHours ?? '0'),
    }
  }
  return baseFields
}

export const formatFormOutput = (values: AddressesFormValues): Partial<UpdateMovePayload> => {
  return {
    locations: {
      origin: formatLocation(values.locations.origin),
      destination: formatLocation(values.locations.destination),
      warehouse: formatLocation(values.locations.warehouse),
      stops: values.locations.stops
        ? (values.locations.stops
            .map(stop => formatLocation(stop))
            ?.filter(s => !!s) ?? []) as MoveStopPayload[]
        : undefined,
    },
  }
}
